import React, { useContext } from 'react';

import MUIDialog, { DialogProps as MUIDialogProps } from '@material-ui/core/Dialog';
import MUIDialogActions from '@material-ui/core/DialogActions';
import MUIDialogContent from '@material-ui/core/DialogContent';
import MUIDialogTitle from '@material-ui/core/DialogTitle';
import MUIIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { type ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  paper: {
    ...common.bigText,
    ...common.flexColumn,
    padding: '120px 180px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    maxWidth: 'initial',
    overflowY: 'initial',
    [theme.breakpoints.down('sm')]: {
      padding: '80px 10px',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },
  },
  dialogSizeXl: {
    maxWidth: '1200px !important',
  },
  dialogSizeLg: {
    maxWidth: '960px !important',
  },
  dialogSizeMd: {
    maxWidth: '480px !important',
  },
  actionablePaper: {
    padding: 0,
    maxWidth: 480,
    width: '100%',
  },
  actionableTitleRoot: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 0,
    paddingBottom: 0,
    [theme.breakpoints.down('sm')]: {
      padding: '0 0 0 1rem',
    },
    height: 56,
    color: theme.palette.grey[800],
    '& > h2': {
      ...common.flexInline,
      ...common.justifyBetween,
      alignItems: 'center',
      height: 56,
      ...common.normalText,
      ...common.fontWeight500,
    },
  },
  actionableContentRoot: {
    ...common.normalText,
    minHeight: 0,
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 1rem',
      maxHeight: '75vh',
      overflow: 'auto',
    },
  },
  actionableActionsRoot: {
    ...common.flexInline,
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    height: 56,
    paddingLeft: 18,
    paddingRight: 18,
    '&.justifyCenter': {
      ...common.justifyCenter,
    },
    '&.justifyStart': {
      ...common.justifyStart,
    },
    '&.justifyEnd': {
      ...common.justifyEnd,
    },
    '&.justifyBetween': {
      ...common.justifyBetween,
    },
  },
  actionableContentDivider: {
    height: 1,
    width: '100%',
    background: theme.palette.grey[200],
  },
}));

const DialogContext = React.createContext<{
  onClose?: ContainerProps['onClose'];
}>({
  onClose: undefined,
});
export interface DialogTitleProps {
  children: React.ReactNode;
  textAlign?: 'center';
  hideCloseButton?: boolean;
}

export const ActionableDialogTitle: React.FC<DialogTitleProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { children, textAlign, hideCloseButton } = props;
  const { onClose } = useContext(DialogContext);

  return (
    <MUIDialogTitle
      classes={{
        root: clsx(classes.actionableTitleRoot),
      }}
    >
      {!hideCloseButton && textAlign === 'center' && (
        <MUIIconButton style={{ opacity: 0, pointerEvents: 'none' }}>
          <CloseIcon />
        </MUIIconButton>
      )}
      <span className={clsx(textAlign === 'center' && [classes.textAlignCenter, classes.flex1])}>
        {children}
      </span>
      {!hideCloseButton && onClose ? (
        <MUIIconButton onClick={onClose}>
          <CloseIcon />
        </MUIIconButton>
      ) : null}
    </MUIDialogTitle>
  );
};

export interface ActionableDialogContentProps
  extends React.ComponentProps<typeof MUIDialogContent> {}

export const ActionableDialogContent: React.FC<ActionableDialogContentProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { children, classes: customClasses, ...rest } = props;

  return (
    <MUIDialogContent
      classes={{ ...customClasses, root: clsx(classes.actionableContentRoot, customClasses?.root) }}
      {...rest}
    >
      {children}
    </MUIDialogContent>
  );
};

export const ActionableDialogContentDivider: React.FC<React.PropsWithChildren<{}>> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { children } = props;

  return <div className={classes.actionableContentDivider}>{children}</div>;
};

export const ActionableDialogActions: React.FC<
  React.PropsWithChildren<{
    justifyContent?: 'center' | 'left' | 'right' | 'between';
  }>
> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { justifyContent, children } = props;

  return (
    <MUIDialogActions
      classes={{
        root: clsx(
          classes.actionableActionsRoot,
          justifyContent == 'center' && 'justifyCenter',
          justifyContent == 'left' && 'justifyStart',
          justifyContent == 'right' && 'justifyEnd',
          justifyContent == 'between' && 'justifyBetween',
        ),
      }}
    >
      {children}
    </MUIDialogActions>
  );
};

export interface ContainerProps {
  open: boolean;
  onClose?: () => void;
  classes?: ClassMap<'paper' | 'root'>;
  muiDialogProps?: Partial<MUIDialogProps>;
  size?: 'md' | 'lg' | 'xl';
  source?: string;
}

export const Dialog: React.FC<React.PropsWithChildren<ContainerProps>> = (props) => {
  const classes = useStyles();

  const { open, onClose, children, classes: customClasses = {}, muiDialogProps } = props;

  return (
    <MUIDialog
      open={open}
      disablePortal
      onClose={onClose}
      {...muiDialogProps}
      classes={{ paper: clsx(classes.paper, customClasses.paper) }}
    >
      {children}
    </MUIDialog>
  );
};

export const ActionableDialog: React.FC<React.PropsWithChildren<ContainerProps>> = (props) => {
  const classes = useStyles();

  const { open, onClose, children, classes: customClasses, muiDialogProps, size, source } = props;

  return (
    <MUIDialog
      open={open}
      disablePortal
      onClose={onClose}
      keepMounted={false}
      {...muiDialogProps}
      classes={{
        paper: clsx(
          classes.paper,
          classes.actionablePaper,
          customClasses?.paper,
          size === 'xl' && classes.dialogSizeXl,
          size === 'lg' && classes.dialogSizeLg,
          size === 'md' && classes.dialogSizeMd,
        ),
        root: customClasses?.root,
      }}
      onKeyUp={(e) => {
        if (import.meta.env.DEV && source) {
          if ((e.target as HTMLInputElement).tagName === 'INPUT') return;
          if (e.key === '.') {
            e.preventDefault();
            e.stopPropagation();
            const { pathname } = new URL(source);
            const vscodeURL = `vscode://file/${PROJECT_ROOT}/src${pathname}`;
            window.open(vscodeURL);
          }
        }
      }}
    >
      <DialogContext.Provider value={{ onClose }}>{children}</DialogContext.Provider>
    </MUIDialog>
  );
};
